import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from "react-router";
import { INavigations, mapNavigationsToProps, AutomaticTaskNavigation, AutomaticTaskNavigationPage } from '../../organisms/automatictask/AutomaticTaskNavigation';
import { IReportDataObject } from '../../../entities/reports/IReportData';
import { IData as IAutomaticTaskData } from "../../../store/automatictask/automaticTaskItem";
import { FetchState } from '../../../entities/FetchState';
import { PageHeader } from '../../organisms/PageHeader';
import { AutomaticTaskEdit } from '../../organisms/automatictask/AutomaticTaskEdit';
import { SaveButton } from '../../atoms/buttons/SaveButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import ErrorMessage from '../../organisms/ErrorMessage';
import { SavingMessage } from '../../organisms/SavingMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';
import { UnsavedChangesBar } from '../../organisms/UnsavedChangesBar';

// Interface for Route Parameters
interface IMatchParams {
  id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch { }

// Properties from State 
interface IStateProps {
  details: IReportDataObject<IAutomaticTaskData>
  updater: IReportDataObject<IAutomaticTaskData>
  deleter: IReportDataObject<IAutomaticTaskData>
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
  details: state.automatictask.automatictaskItem.Item,
  updater: state.automatictask.automatictaskItem.UpdateItem,
  deleter: state.automatictask.automatictaskItem.DeleteItem
});

// Outgoing functions to Dispatch
interface IDispatchProps {
  navigations: INavigations;
  onLoadDetail: (id: string) => void;
  onEdit: (details: IAutomaticTaskData) => void;
  onDelete: (id: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
  navigations: mapNavigationsToProps(dispatch),
  onLoadDetail: (id: string) => dispatch(RootActionCreators.Automatictask.automatictaskItem.FetchData(id)),
  onEdit: (details: IAutomaticTaskData) => dispatch(RootActionCreators.Automatictask.automatictaskItem.UpdateData(
    details.streamId, 
    details.title, 
    details.description, 
    details.completionPeriod, 
    details.forAdmin, 
    details.mandatory, 
    details.fileStatusId
  )),
  onDelete: (id: string) => dispatch(RootActionCreators.Automatictask.automatictaskItem.DeleteData(id))
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const AutomaticTaskEditPage = (props: AllProps) => {
  const { t: tAutomaticTask } = useTranslation('automatictask');
  const { t: tCommon } = useTranslation('common');

  const isError = props.details.state === FetchState.Error;
  const isLoading = props.details.state === FetchState.NotFetched || props.details.state === FetchState.Busy || isError;
  const isDeleting = props.deleter.state === FetchState.Busy;
  const hasDeleteError = props.deleter.state === FetchState.Error;
  
  const [details, setDetails] = React.useState(props.details.data);
  const [detailsValid, setDetailsValid] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  // Reset unsaved changes flag after successful save
  React.useEffect(() => {
    if (props.updater.state === FetchState.Success) {
      setHasUnsavedChanges(false);
    }
  }, [props.updater.state]);

  // Load data when component mounts
  React.useEffect(() => {
    const id = props.match.params.id;
    if (props.details.state === FetchState.NotFetched) {
      setDetailsValid(false);
      props.onLoadDetail(id);
    } else if (props.details.state === FetchState.Success) {
      if (props.details.data.streamId !== id) {
        setDetailsValid(false);
        props.onLoadDetail(id);
      }
    }
    if (props.details.state === FetchState.Success && props.details.data) {
      setDetailsValid(true);
      setDetails(props.details.data);
    }
  }, [props.details.state, props.match.params.id, props.details?.data.streamId]);

  return (
    <AutomaticTaskNavigation navigations={props.navigations} selected={AutomaticTaskNavigationPage.Edit}>
      <UnsavedChangesBar show={hasUnsavedChanges} />

      <PageHeader
        title={tAutomaticTask('title')}
        subTitle={tAutomaticTask('editTitle', { title: details.title || '' })}
        explainText={tAutomaticTask('editExplanation')}
      />

      {isDeleting && <DeletingMessage entity={tAutomaticTask('automaticTask')} />}
      {props.updater.state === FetchState.Busy && !isError && <SavingMessage entity={tAutomaticTask('automaticTask')} />}

      <AutomaticTaskEdit
        data={details}
        loading={isLoading}
        disabled={false}
        onUpdate={(details, valid) => {
          setDetails(details);
          setDetailsValid(valid);
        }}
        onDirtyChange={() => setHasUnsavedChanges(true)}
        actions={
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <SaveButton disabled={!detailsValid || isLoading} onClick={() => props.onEdit(details)} />
            <RefreshButton onClick={() => props.onLoadDetail(details.streamId)} disabled={props.details.state === FetchState.Busy} />
            <DeleteButton 
              disabled={isLoading} 
              onClick={() => props.onDelete(details.streamId)} 
              deleteWarningMessage={tAutomaticTask('deleteWarning', { title: details.title || '' })} 
            />
          </div>
        }
      />

      {isError && <ErrorMessage 
        message={tCommon('errorLoadingData')} 
        title={tCommon('errorLoadingDataTitle')} 
        errorDetails={props.details.errorMessage} 
        options={[{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} 
      />}

      {props.updater.state === FetchState.Error && !isError && 
        <ErrorMessage 
          title={tCommon('savingErrorTitle')} 
          message={tCommon('savingErrorXMessage', {x: props.updater.errorMessage})} 
          canClose 
          options={[]} 
          errorDetails={JSON.stringify(details)} 
        />
      }
      
      {hasDeleteError && 
        <ErrorMessage 
          title={tCommon('deletingErrorTitle')} 
          message={tCommon('deletingErrorXMessage', {x: props.deleter.errorMessage})} 
          canClose 
          options={[]} 
          errorDetails={props.deleter.errorMessage} 
        />
      }
    </AutomaticTaskNavigation>
  );
}

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(AutomaticTaskEditPage);



